/**
 * OrderTimeline - Shows a chronological feed of market activity.
 * Displays recent orders and trades as they happen, with educational context.
 */

import { memo, useEffect, useRef } from 'react'
import { ArrowUpCircle, ArrowDownCircle, Zap, Clock } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { OrderSide } from '../../engine/types'

/**
 * An event in the order timeline - either an order placed or a trade executed.
 */
export interface TimelineEvent {
  id: string
  type: 'order' | 'trade'
  timestamp: number
  side: OrderSide
  price: number
  quantity: number
  orderType?: 'limit' | 'market'
  isUserOrder?: boolean
}

export interface OrderTimelineProps {
  /** Recent events to display */
  events: TimelineEvent[]
  /** Maximum events to show */
  maxEvents?: number
  /** Height of the timeline container */
  height?: number
  /** Optional className */
  className?: string
}

/**
 * Format timestamp as relative time (e.g., "2s ago", "1m ago").
 */
function formatRelativeTime(timestamp: number): string {
  const now = Date.now()
  const diff = now - timestamp

  if (diff < 1000) return 'now'
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  return `${Math.floor(diff / 3600000)}h ago`
}

/**
 * Get a human-readable description of the event.
 */
function getEventDescription(event: TimelineEvent): string {
  if (event.type === 'trade') {
    return `${event.quantity} shares traded at ${Math.round(event.price * 100)}¢`
  }

  const action = event.side === 'buy' ? 'Buy' : 'Sell'
  const orderType = event.orderType === 'market' ? 'market order' : `limit at ${Math.round(event.price * 100)}¢`
  return `${action} ${event.quantity} shares (${orderType})`
}

/**
 * OrderTimeline displays a live feed of market activity.
 */
export const OrderTimeline = memo(function OrderTimeline({
  events,
  maxEvents = 20,
  height = 200,
  className,
}: OrderTimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to top when new events come in
  useEffect(() => {
    if (scrollRef.current && events.length > 0) {
      scrollRef.current.scrollTop = 0
    }
  }, [events.length])

  const displayEvents = events.slice(0, maxEvents)

  if (displayEvents.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-border bg-card p-4',
          className
        )}
        style={{ height }}
      >
        <div className="text-center">
          <Clock className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">
            No activity yet. Press play or place an order to see the timeline.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn('rounded-lg border border-border bg-card', className)}
      role="log"
      aria-label="Order timeline"
      aria-live="polite"
    >
      <div className="border-b border-border px-4 py-2">
        <h3 className="text-sm font-medium text-foreground">Activity Feed</h3>
      </div>

      <div
        ref={scrollRef}
        className="overflow-y-auto p-2"
        style={{ height: height - 40 }}
      >
        <div className="space-y-1">
          {displayEvents.map((event, index) => (
            <div
              key={event.id}
              className={cn(
                'flex items-start gap-2 rounded-md px-2 py-1.5 transition-colors',
                event.isUserOrder && 'bg-primary/10',
                index === 0 && 'animate-pulse bg-muted/50'
              )}
            >
              {/* Icon */}
              <div className="mt-0.5 flex-shrink-0">
                {event.type === 'trade' ? (
                  <Zap className="h-4 w-4 text-yellow-500" />
                ) : event.side === 'buy' ? (
                  <ArrowUpCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <ArrowDownCircle className="h-4 w-4 text-red-500" />
                )}
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'text-xs font-medium',
                      event.type === 'trade'
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : event.side === 'buy'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                    )}
                  >
                    {event.type === 'trade' ? 'Trade' : event.side === 'buy' ? 'Buy' : 'Sell'}
                  </span>
                  {event.isUserOrder && (
                    <span className="rounded bg-primary px-1 py-0.5 text-[10px] font-medium text-primary-foreground">
                      You
                    </span>
                  )}
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    {formatRelativeTime(event.timestamp)}
                  </span>
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {getEventDescription(event)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Educational footer */}
      <div className="border-t border-border px-3 py-2">
        <p className="text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <ArrowUpCircle className="h-3 w-3 text-green-500" /> Buy orders
          </span>
          {' · '}
          <span className="inline-flex items-center gap-1">
            <ArrowDownCircle className="h-3 w-3 text-red-500" /> Sell orders
          </span>
          {' · '}
          <span className="inline-flex items-center gap-1">
            <Zap className="h-3 w-3 text-yellow-500" /> Trades
          </span>
        </p>
      </div>
    </div>
  )
})
